import React, { useState } from "react";
import "./styles/AIEditor.css";

const QUICK_ACTIONS = [
  { key: "improve", label: "Improve" },
  { key: "proofread", label: "Proofread" },
  { key: "shorten", label: "Shorten" },
  { key: "expand", label: "Expand" },
  { key: "rewrite", label: "Rewrite" },
  { key: "headline", label: "Headline ideas" },
];

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Convert the editor's HTML into plain text to send to the AI.
function htmlToText(html) {
  if (!html) return "";
  const withBreaks = html
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, "\n\n")
    .replace(/<br\s*\/?>(?!\n)/gi, "\n");
  const tmp = document.createElement("div");
  tmp.innerHTML = withBreaks;
  const text = tmp.textContent || tmp.innerText || "";
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

// Convert AI plain-text prose back into safe paragraph HTML for the editor.
function textToHtml(text) {
  return String(text || "")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// Parse a numbered/bulleted headline list into individual lines.
function parseHeadlines(text) {
  return String(text || "")
    .split(/\n+/)
    .map((line) => line.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, "").trim())
    .filter(Boolean);
}

const AIEditor = ({ token, title, contentHtml, onApplyContent, onInsertContent, onSetTitle }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { text, action }

  const run = async (action, instruction) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const resp = await fetch("/api/ai-editor", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action,
          instruction: instruction || "",
          title: title || "",
          content: htmlToText(contentHtml),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.refused) {
        setError(data.reply || "The AI Editor couldn't complete that request.");
        return;
      }
      setResult({ text: data.reply, action: data.action || action });
    } catch (err) {
      setError("Network error reaching the AI Editor. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result?.text) onApplyContent(textToHtml(result.text));
    setResult(null);
  };

  const handleInsert = () => {
    if (result?.text) onInsertContent(textToHtml(result.text));
    setResult(null);
  };

  const pickHeadline = (line) => {
    if (onSetTitle) onSetTitle(line);
    setResult(null);
  };

  return (
    <div className="ai-editor">
      <button
        type="button"
        className={`ai-editor-toggle ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="ai-editor-spark">✦</span> AI Editor
        <span className="ai-editor-chevron">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="ai-editor-panel">
          <p className="ai-editor-hint">
            Your assistant for drafting and editing. It writes in clean news style and never uses em dashes.
          </p>

          <div className="ai-editor-actions">
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                className="ai-editor-action"
                disabled={loading}
                onClick={() => run(a.key)}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              className="ai-editor-action ai-editor-action-draft"
              disabled={loading}
              onClick={() => run("draft")}
            >
              Draft from title/notes
            </button>
          </div>

          <div className="ai-editor-custom">
            <input
              type="text"
              value={custom}
              placeholder="Or tell the AI Editor exactly what to do..."
              onChange={(e) => setCustom(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (custom.trim()) run("custom", custom.trim());
                }
              }}
            />
            <button
              type="button"
              className="ai-editor-ask"
              disabled={loading || !custom.trim()}
              onClick={() => run("custom", custom.trim())}
            >
              Ask
            </button>
          </div>

          {loading && <div className="ai-editor-loading">The AI Editor is working…</div>}
          {error && <div className="ai-editor-error">{error}</div>}

          {result && result.action === "headline" && (
            <div className="ai-editor-result">
              <div className="ai-editor-result-label">Pick a headline:</div>
              <div className="ai-editor-headlines">
                {parseHeadlines(result.text).map((line, i) => (
                  <button key={i} type="button" className="ai-editor-headline" onClick={() => pickHeadline(line)}>
                    {line}
                  </button>
                ))}
              </div>
              <div className="ai-editor-result-buttons">
                <button type="button" className="ai-editor-dismiss" onClick={() => setResult(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {result && result.action !== "headline" && (
            <div className="ai-editor-result">
              <div className="ai-editor-result-label">Suggested text:</div>
              <div className="ai-editor-preview">{result.text}</div>
              <div className="ai-editor-result-buttons">
                <button type="button" className="ai-editor-apply" onClick={handleApply}>
                  Replace content
                </button>
                <button type="button" className="ai-editor-insert" onClick={handleInsert}>
                  Insert at end
                </button>
                <button type="button" className="ai-editor-dismiss" onClick={() => setResult(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIEditor;
